import { Injectable, HostListener, NgZone } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { MasterViewModel } from '../../../base-classes/master-view-model';
import { HttpClient } from '@angular/common/http';
import { TestRecording } from '../models/test-recorder.model';
import { ConfirmationService } from 'primeng/api';
import { environment } from '../../../../environments/environment';
import { TestRecorderService } from '../services/test-recorder.service';
import { ToastService } from '../../../toast/toast.service';
import { ActionRecorderService, RecordingSession, RecordedAction } from '../../../services/action-recorder.service';
import { TestStep } from '../../../services/test-executor.service';
import { Subscription, firstValueFrom } from 'rxjs';
import { MESSAGE_CONFIG } from '../../../config/menu-config';
import { ValidationMessagesHelper } from '../helpers/validation-messages.helper';
import { ScenarioService } from '../../scenario/services/scenario.service';
import { SkeletonService } from '../../../services/skeleton.service';
import { StepsService } from '../../steps/services/steps.service';
import { StepPlaceholderService } from '../../../services/step-placeholder.service';
import { ScreenMasterService } from '../../screen-master/services/screen-master.service';
import { ScreenMasterHelper } from '../../screen-master/helpers/screen-master.helper';
import { AuthService } from '../../../auth/login/services/auth.service';

@Injectable()
export class TestRecorderViewModel extends MasterViewModel<TestRecording> {
  testRecorderService: TestRecorderService;
  private subscriptions: Subscription[] = [];
  scenarioIdField: string = '';
  runName: string = '';
  description: string = '';
  
  // Screen capture properties
  mode: 'test-recorder' | 'screen-capture' = 'test-recorder';
  screenId: string = '';
  screenEditId: string = '';
  screenName: string = '';
  screenDescription: string = '';
  matchRules: any[] = [];
  minMatch: number = 2;
  capturedIdentifiers: any | null = null;
  isCapturingScreen: boolean = false;
  
  // Rule form properties
  showRuleDialog: boolean = false;
  ruleType: 'title_contains' | 'url_contains' | 'element_exists' | 'heading_contains' | 'breadcrumb_contains' = 'title_contains';
  ruleSelector: string = '';
  ruleText: string = '';
  ruleUrl: string = '';
  editingRuleIndex: number = -1;
  fieldErrors: Record<string, string> = {};

  url: string = 'https://impl.wd12.myworkday.com/wday/authgwy/erpa_amu1/login.htmld';
  clientId: string = '';
  clientOptions: any[] = [];
  clientPackageMappings: any[] = [];
  packageDefinitionId: string = '';
  functionalAreaId: string = '';
  testTypeId: string = '';
  packageOptions: any[] = [];
  functionalAreaOptions: any[] = [];
  testTypeOptions: any[] = [];
  allReferenceValues: any[] = []; // Store all reference values for ID mapping
  filterClient: string = '';
  isClientFilterDisabled: boolean = false;
  filterPackage: string = '';
  filterFunctionalArea: string = '';
  filterTestType: string = '';
  filteredTestTypeList: any[] = [];
  filteredFunctionalAreaList: any[] = [];
  isRecording: boolean = false;
  isPaused: boolean = false;
  currentSession: RecordingSession | null = null;
  liveSteps: TestStep[] = [];
  private lastCapturedElement: RecordedAction['element'] | null = null;
  verifyExpected: string = '';
  verifyOperator: 'equals' | 'contains' | 'containsAll' | 'matches' | 'is' = 'contains';
  pageVerifyText: string = '';
  labelVerifyLabel: string = '';
  labelVerifyExpected: string = '';
  labelVerifyOperator: 'equals' | 'contains' | 'containsAll' | 'matches' | 'is' = 'contains';
  
  // Element Absence validation properties
  notExistElementName: string = '';
  absenceTimeout: number = 3000;
  pageNotContainText: string = '';
  tableNotContainText: string = '';
  tableNotContainSearchAllPages: boolean = false;
  columnNotContainName: string = '';
  columnNotContainValue: string = '';
  columnNotContainSearchAllPages: boolean = false;
  
  // Table validation properties
  tableValidationActive: boolean = false;
  isCapturingTable: boolean = false;
  isDiscoveringTables: boolean = false;
  availableTables: Array<{ index: number; selector: string; label: string; columnNames: string[]; columnCount: number; rowCount: number }> = [];
  selectedTableIndex: number = -1;
  tableInfo: { 
    tableSelector: string; 
    tableId: string; 
    columnNames: string[]; 
    columnCount: number; 
    hasPagination: boolean;
    hasMultiValueColumns?: boolean;
    multiValueColumns?: Array<{ name: string; index: number; valueCount: number; detectionMethod: string }>;
  } | null = null;
  currentValidations: Array<{ columnPairs: Array<{ column: string; value: string }> }> = [];
  newValidationPairs: Array<{ column: string; value: string; operator?: 'equals' | 'contains' | 'notEquals' | 'notContains' | 'greaterThan' | 'lessThan' | 'startsWith' | 'endsWith' | 'isEmpty' | 'isNotEmpty' }> = [{ column: '', value: '', operator: 'equals' }];
  tableSearchAllPages: boolean = false;
  private currentTableStepId: string = '';
  
  recordings: any[] = [];
  totalRecordings: number = 0;
  currentPage: number = 1;
  pageSize: number = 10;
  searchTerm: string = '';
  sortField: string = 'createdOn';
  sortOrder: string = 'DESC';
  isLoadingRecordings: boolean = false;
  private searchDebounceTimer: any = null;
  private filtersReady: boolean = false;
  private loadRecordingsSub: Subscription | null = null;
  editRecordingId: string = '';
  private loadedRecordingClientId: string | null = null;
  isCloneMode: boolean = false;
  isExecuting: boolean = false;
  currentExecutingStep: number = -1;
  executedSteps = new Set<number>();
  failedSteps = new Set<number>();
  stepErrors = new Map<number, string>();
  stepResults = new Map<number, any>();
  private stopRequested: boolean = false;
  isSaving: boolean = false;
  isSkeleton: boolean = false;
  splitPx: number = 440;
  dragging: boolean = false;
  editMenuIndex: number = -1;
  editTargetText: string = '';
  editValue: string = '';
  editCheckTag: string = '';
  waitMenuIndex: number = -1;
  waitCustomMs: string = '1000';
  addMenuIndex: number = -1;
  insertRecordIndex: number = -1;
  insertRecordSub?: any;
  packageId: string = '';
  scenarioId: string = '';
  deleteOldSteps: boolean = false;
  oldStepSource: string | null = null;
  // Defaults provided via navigation from Scenario list
  private _defaultPackageName: string = '';
  private _defaultpackageId: string = '';
  private _defaultFunctionalAreaId: string = '';
  private _defaultTestTypeName: string = '';
  private _defaultScenarioName: string = '';
  private _defaultScenarioDescription: string = '';
  private originFrom: 'scenario' | 'recorder-list' | 'unknown' = 'recorder-list';

  get filteredFunctionalAreas(): any[] {
    if (!this.packageDefinitionId) {
      return this.functionalAreaOptions;
    }

    const selectedMapping = this.clientPackageMappings.find(
      (m: any) => (m.package?.id || m.packageId) === this.packageDefinitionId
    );

    return this.functionalAreaOptions.filter((fa: any) => {
      const matchesPackage = fa.package?.id === this.packageDefinitionId;
      const isCurrentSelection = fa.id === this.functionalAreaId;

      if (!matchesPackage && !isCurrentSelection) return false;

      if (this.clientId && selectedMapping) {
        return fa.clientPackageMappingId === selectedMapping.id || isCurrentSelection;
      }
      return !fa.clientPackageMappingId || isCurrentSelection;
    });
  }

  /**
   * Get test type options filtered by selected package
   */
  get filteredTestTypeOptions(): any[] {
    if (this.filteredTestTypeList.length > 0) {
      return this.filteredTestTypeList;
    }
    return this.testTypeOptions;
  }

  /**
   * Get functional area options with test type appended for display
   */
  get functionalAreaOptionsWithTestType(): any[] {
    let areas = this.filteredFunctionalAreaList || [];
    if (this.filterTestType) {
      areas = areas.filter((fa: any) => fa.testType?.id === this.filterTestType);
    }
    const seen = new Set<string>();
    const result: any[] = [];
    for (const fa of areas) {
      if (!fa?.id) continue;
      const testType = fa.testType;
      const key = `${fa.id}|${testType?.id || ''}`;
      if (seen.has(key)) continue;
      seen.add(key);
      result.push({
        ...fa,
        id: key,
        functionalAreaId: fa.id,
        testTypeId: testType?.id || '',
        displayName: fa.name + (testType?.referenceValue ? ` (${testType.referenceValue})` : '')
      });
    }
    return result;
  }

  /**
   * Get filtered functional areas with test type appended for display
   */
  get filteredFunctionalAreasWithTestType(): any[] {
    return this.filteredFunctionalAreas.map((fa: any) => ({
      ...fa,
      displayName: fa.name + (fa.testType?.referenceValue ? ` (${fa.testType.referenceValue})` : '')
    }));
  }

  constructor(
    http: HttpClient,
    private messageService: ToastService,
    private confirmationService: ConfirmationService,
    private actionRecorder: ActionRecorderService,
    private router: Router,
    private route: ActivatedRoute,
    private ngZone: NgZone,
    private scenarioService: ScenarioService,
    private stepsService: StepsService,
    private stepPlaceholderService: StepPlaceholderService,
    private screenMasterService: ScreenMasterService,
    private authService: AuthService,
    private skeletonService: SkeletonService
  ) {
    const baseUrl = `${environment.apiUrl}/recordings`;
    const moduleName = 'Test Recorder';
    super(http, baseUrl, moduleName);
    this.testRecorderService = new TestRecorderService(http);
  }

  public init(): void {
    if (this.isSystemUser()) {
      this.filterClient = 'default';
      this.filtersReady = true;
    }
    this.getReferenceList();

    const navigation = this.router.getCurrentNavigation();
    const routerState = navigation?.extras?.state || (window.history.state as any);

    if (routerState?.['recording']) {
      const recording = routerState['recording'];
      this.editRecordingId = (recording.isActive === false) ? '' : (routerState['editRecordingId'] || '');
      this.isCloneMode = routerState['isCloneMode'] || false;
      this.packageId = recording.packageId || '';
      this.runName = recording.runName || '';
      this.description = recording.description || '';
      this.url = recording.baseUrl || '';
      this.packageDefinitionId = recording.packageDefinitionId || '';
      this.functionalAreaId = recording.functionalAreaId || '';
      this.testTypeId = recording.testTypeId || '';
      this.scenarioIdField = routerState['scenarioId'] || recording.scenarioCode || '';

      // Pre-populate client from the mapping relation or router state
      const stateClientId = routerState?.['clientId'];
      if (recording.clientPackageMapping?.clientId) {
        this.clientId = recording.clientPackageMapping.clientId;
        this.loadedRecordingClientId = recording.clientPackageMapping.clientId;
        // Load packages for this client so the dropdown is populated
        this.testRecorderService.getClientPackages(this.clientId).subscribe({
          next: (mappings: any[]) => {
            this.clientPackageMappings = mappings;
            this.packageOptions = mappings
              .filter((m: any) => m.isActive)
              .map((m: any) => ({
                id: m.package?.id || m.packageId,
                packageName: m.package?.packageName || m.package?.name || m.packageId,
                mappingId: m.id
              }));
          },
          error: () => {
            this.clientPackageMappings = [];
            this.packageOptions = [];
          }
        });
      } else if (stateClientId && stateClientId !== 'default') {
        this.clientId = stateClientId;
        this.loadedRecordingClientId = null;
        this.testRecorderService.getClientPackages(this.clientId).subscribe({
          next: (mappings: any[]) => {
            this.clientPackageMappings = mappings;
            this.packageOptions = mappings
              .filter((m: any) => m.isActive)
              .map((m: any) => ({
                id: m.package?.id || m.packageId,
                packageName: m.package?.packageName || m.package?.name || m.packageId,
                mappingId: m.id
              }));
          },
          error: () => {
            this.clientPackageMappings = [];
            this.packageOptions = [];
          }
        });
      } else {
        this.clientId = 'default';
        this.testRecorderService.getPackages().subscribe({
          next: (packages: any[]) => {
            this.packageOptions = (packages || []).map((p: any) => ({
              id: p.id,
              packageName: p.packageName || p.name
            }));
          },
          error: () => { this.packageOptions = []; }
        });
      }

      this.isSkeleton = recording.isSkeleton || false;

      // Deep copy steps to prevent shared selector references between table steps
      const loadedSteps = (recording.steps || []).map((s: any) => ({
        ...s,
        selector: s.selector ? JSON.parse(JSON.stringify(s.selector)) : {}
      }));

      const session: RecordingSession = {
        recordingId: recording.recordingId || `rec-${Date.now()}`,
        runName: recording.runName || '',
        description: recording.description || '',
        baseUrl: recording.baseUrl || '',
        status: 'completed',
        recordedAt: new Date().toISOString(),
        actions: recording.rawActions || [],
        executableSteps: loadedSteps,
        dataParameters: recording.dataParameters || {},
        metadata: recording.metadata || {
          totalSteps: (recording.steps || []).length,
          estimatedDuration: 0,
          tags: [],
          category: 'recorded'
        }
      };

      try {
        const recorderService = this.actionRecorder as any;
        if (recorderService.recordingSession$) {
          recorderService.recordingSession$.next(session);
        }
        if (recorderService.liveExecutableSteps$) {
          recorderService.liveExecutableSteps$.next([...loadedSteps]);
        }
        if (recorderService.liveActions$) {
          recorderService.liveActions$.next(recording.rawActions || []);
        }
      } catch (error: any) {
        this.currentSession = session;
        this.liveSteps = [...loadedSteps];
      }

      setTimeout(() => {
        this.initializeSplitView();
        // Don't auto-load table validation — user should discover tables fresh each time
      }, 100);
    }

    // Read query params
    const isRecordRoute = (this.router.url || '').includes('/test-studio/recorder-record');
    const routeSub = this.route.queryParams.subscribe(params => {
      this.packageId = params['packageId'] || '';
      this.scenarioId = params['scenarioId'] || '';
      this.originFrom = (params['from'] as any) || 'recorder-list';
      this.mode = (params['mode'] as any) || 'test-recorder';

      // Read from router state if available (for new recording from scenario)
      if (routerState) {
        this._defaultPackageName = routerState['packageName'] || this._defaultPackageName;
        this._defaultFunctionalAreaId = routerState['functionalAreaId'] || this._defaultFunctionalAreaId;
        this._defaultTestTypeName = routerState['testTypeName'] || this._defaultTestTypeName;
        this._defaultScenarioName = routerState['scenarioName'] || this._defaultScenarioName;
        this._defaultpackageId = routerState['packageId'] || this._defaultpackageId;
        this._defaultScenarioDescription = routerState['scenarioDescription'] || this._defaultScenarioDescription;
        this.packageDefinitionId = routerState['packageDefinitionId'] || this.packageDefinitionId || '';
        this.functionalAreaId = routerState['functionalAreaId'] || this.functionalAreaId || '';
        this.testTypeId = routerState['testTypeId'] || this.testTypeId || '';
        
        // Sync both packageId and packageDefinitionId from router state
        const statePackageDefId = routerState['packageDefinitionId'];
        const statePackageId = routerState['packageId'];
        
        if (statePackageDefId) {
          this.packageDefinitionId = statePackageDefId;
          this.packageId = statePackageDefId; // Keep in sync
        } else if (statePackageId) {
          this.packageId = statePackageId;
          this.packageDefinitionId = statePackageId; // Keep in sync
        }
        
        this.scenarioIdField = routerState['scenarioIdField'] || this.scenarioIdField || '';
        this.clientId = routerState['clientId'] || this.clientId || '';
        this.deleteOldSteps = routerState['deleteOldSteps'] || false;
        this.oldStepSource = routerState['oldStepSource'] || null;
      }

      // Read screen data for edit mode (screen-capture mode)
      if (routerState?.['screen']) {
        const screen = routerState['screen'];
        this.screenEditId = screen.id || '';
        // Handle both API field name (screenName) and client field name (name)
        this.screenName = screen.screenName || screen.name || '';
        this.screenDescription = screen.description || '';
        this.matchRules = screen.matchRules || [];
        this.minMatch = screen.minMatch || 2;
        
        // Load steps into ActionRecorderService so subscriptions work correctly
        const fallbackSteps = screen.fallbackSteps || [];
        
        try {
          const recorderService = this.actionRecorder as any;
          if (recorderService.liveExecutableSteps$) {
            recorderService.liveExecutableSteps$.next(fallbackSteps);
          }
        } catch (error: any) {
          // Fallback: assign directly if service doesn't support it
          this.liveSteps = fallbackSteps;
        }
      }

      // Attempt to apply defaults immediately if options are available
      this.applyDefaultsFromParams();

      if (isRecordRoute) {
        setTimeout(() => {
          this.initializeSplitView();
        }, 50);
      }
    });
    this.subscriptions.push(routeSub);

    const recordingSub = this.actionRecorder.isRecording.subscribe(recording => {
      this.ngZone.run(() => {
        this.isRecording = recording;
      });
    });
    this.subscriptions.push(recordingSub);

    const sessionSub = this.actionRecorder.currentSession.subscribe(session => {
      this.ngZone.run(() => {
        this.currentSession = session;
        if (session) {
          this.isPaused = session.status === 'paused';
        }
      });
    });
    this.subscriptions.push(sessionSub);

    const stepsSub = this.actionRecorder.liveExecutableSteps.subscribe(steps => {
      // Deep copy ALL table steps to isolate selector references
      const safeSteps = steps.map((s: any) => {
        if (s.action === 'verifyTableMultiple' && s.selector) {
          const copy = JSON.parse(JSON.stringify(s));
          return copy;
        }
        return s;
      });
      this.ngZone.run(() => {
        this.liveSteps = safeSteps;
      });
    });
    this.subscriptions.push(stepsSub);

    const actionsSub = this.actionRecorder.liveActions.subscribe(actions => {
      this.ngZone.run(() => {
        const last = actions[actions.length - 1];
        this.lastCapturedElement = last?.element || null;
      });
    });
    this.subscriptions.push(actionsSub);

    // Subscribe to table interactions for automatic detection
    const tableInteractionSub = this.actionRecorder.tableInteractionDetected.subscribe(tableCtx => {
      if (tableCtx && this.isRecording) {
        this.ngZone.run(() => {
          // Show info toast with hint
          this.messageService.info(
            `Table interaction detected: ${tableCtx.columnName || 'Column'} = "${tableCtx.cellValue}". ` +
            `Use "Table Validation" tab to validate table data.`,
            'Table Cell Clicked'
          );
          
          // Optional: Auto-switch to Table Validation tab or highlight it
        });
      }
    });
    this.subscriptions.push(tableInteractionSub);
  }

  public destroy(): void {
    this.subscriptions.forEach(sub => {
      try {
        sub.unsubscribe();
      } catch (error) {
      }
    });
    this.subscriptions = [];

    if (this.loadRecordingsSub) {
      this.loadRecordingsSub.unsubscribe();
      this.loadRecordingsSub = null;
    }

    if (this.isRecording) {
      this.stopRecording();
    }

    this.disableBrowserView();
  }

  // Dropdown change handlers
  onClientChange(): void {
    this.packageDefinitionId = '';
    this.packageId = '';
    this.functionalAreaId = '';
    this.testTypeId = '';
    this.clientPackageMappings = [];
    this.packageOptions = [];

    if (this.clientId === 'default') {
      this.testRecorderService.getPackages().subscribe({
        next: (packages: any[]) => {
          this.packageOptions = packages.map((pkg: any) => ({
            id: pkg.id,
            packageName: pkg.packageName || pkg.name
          }));
        },
        error: () => {
          this.packageOptions = [];
        }
      });
    } else if (this.clientId) {
      this.testRecorderService.getClientPackages(this.clientId).subscribe({
        next: (mappings: any[]) => {
          this.clientPackageMappings = mappings;
          this.packageOptions = mappings
            .filter((m: any) => m.isActive)
            .map((m: any) => ({
              id: m.package?.id || m.packageId,
              packageName: m.package?.packageName || m.package?.name || m.packageId,
              mappingId: m.id
            }));
        },
        error: () => {
          this.clientPackageMappings = [];
          this.packageOptions = [];
        }
      });
    }
  }

  onPackageChange(): void {
    // Sync packageId with packageDefinitionId whenever dropdown changes
    this.packageId = this.packageDefinitionId;

    // When package changes, clear functional area and test type if they don't belong to the new package
    if (this.functionalAreaId) {
      const stillValid = this.filteredFunctionalAreas.some((fa: any) => fa.id === this.functionalAreaId);
      if (!stillValid) {
        this.functionalAreaId = '';
        this.testTypeId = '';
      }
    }
  }



  addVerification(assert: boolean = false): void {
    if (!this.lastCapturedElement) {
      this.messageService.error(ValidationMessagesHelper.NO_ELEMENT_TO_VERIFY);
      return;
    }

    const expected = this.verifyExpected.trim();
    this.actionRecorder.addVerification({
      assert,
      element: this.lastCapturedElement,
      expected: expected.length > 0 ? expected : undefined,
      operator: this.verifyOperator,
      timeoutMs: 8000
    });


    this.verifyExpected = '';
    this.messageService.success(ValidationMessagesHelper.getVerifyStepAdded(assert));
  }

  async initializeSplitView(): Promise<void> {
    try {
      await (window as any).host?.setSplit(this.splitPx);
      await (window as any).host?.captureEnable(true, {
        featureId: this.scenarioId || 'test-recorder',
        initialUrl: this.url || 'about:blank'
      });
    } catch (error) {
    }
  }

  async disableBrowserView(): Promise<void> {
    try {
      await (window as any).host?.captureEnable(false);
    } catch (error) {
    }
  }

  async startRecording(): Promise<void> {
    if (!this.scenarioIdField.trim()) {
      this.messageService.error('Please enter Scenario ID');
      return;
    }

    if (!this.runName.trim()) {
      this.messageService.error(ValidationMessagesHelper.ENTER_RUN_NAME);
      return;
    }

    if (!this.url.trim()) {
      this.messageService.error(ValidationMessagesHelper.ENTER_URL);
      return;
    }

    try {
      await (window as any).host?.navigate(this.url);

      // Generate recordingId with scenario ID appended: rec-timestamp-scenarioId
      const timestamp = Date.now();
      const customRecordingId = `rec-${timestamp}-${this.scenarioIdField}`;
      const session = await this.actionRecorder.startRecording(this.runName, this.url, this.description, customRecordingId);
      this.currentSession = session;
      this.editMenuIndex = -1; this.editTargetText = ''; this.editValue = '';

      this.messageService.success(ValidationMessagesHelper.getRecordingStarted(this.runName));
    } catch (error: any) {
      this.messageService.error(ValidationMessagesHelper.getFailedStartRecording(error.message));
    }
  }

  async clearRun(): Promise<void> {
    try {
      await (window as any).host?.recordingEnable(false);

      this.actionRecorder.clearSession();
      this.currentSession = null;
      this.isPaused = false;
      this.executedSteps.clear();
      this.failedSteps.clear();
      this.stepErrors.clear();
      this.stepResults.clear();
      this.editMenuIndex = -1; this.editTargetText = ''; this.editValue = '';
      this.insertRecordIndex = -1; if (this.insertRecordSub) { try { this.insertRecordSub.unsubscribe(); } catch (_) { } this.insertRecordSub = undefined; }

      this.messageService.success(ValidationMessagesHelper.CLEARED_RUN);
    } catch (error: any) {
      this.messageService.error(ValidationMessagesHelper.getFailedClear(error.message));
    }
  }

  async stopRecording(): Promise<void> {
    try {
      const session = await this.actionRecorder.stopRecording();

      if (session) {
        this.messageService.success(ValidationMessagesHelper.getRecordingCompleted(session.metadata.totalSteps));

        this.editMenuIndex = -1; this.editTargetText = ''; this.editValue = '';
        this.insertRecordIndex = -1; if (this.insertRecordSub) { try { this.insertRecordSub.unsubscribe(); } catch (_) { } this.insertRecordSub = undefined; }
      }
    } catch (error: any) {
      this.messageService.error(ValidationMessagesHelper.getFailedStopRecording(error.message));
    }
  }


  async pauseResumeRecording(): Promise<void> {
    try {
      await this.actionRecorder.pauseRecording();

      if (this.isPaused) {
        this.messageService.info(ValidationMessagesHelper.RECORDING_RESUMED);
      } else {
        this.messageService.info(ValidationMessagesHelper.RECORDING_PAUSED);
      }
    } catch (error: any) {
      this.messageService.error(ValidationMessagesHelper.getFailedPauseResume(error.message));
    }
  }

  async saveRecording(): Promise<void> {
    try {
      this.isSaving = true;
      let session: RecordingSession | null = null;
      if (this.editRecordingId && this.currentSession) {
        session = this.currentSession;
        session.runName = this.runName;
        session.description = this.description;
        session.baseUrl = this.url;
        session.executableSteps = this.liveSteps;
        session.metadata.totalSteps = this.liveSteps.length;
      } else if (this.isRecording) {
        session = await this.actionRecorder.stopRecording();
      } else if (this.currentSession) {
        session = this.currentSession;
        session.runName = this.runName;
        session.description = this.description;
        session.baseUrl = this.url;
        session.executableSteps = this.liveSteps;
        session.metadata.totalSteps = this.liveSteps.length;
      }

      if (!session) {
        this.messageService.error(ValidationMessagesHelper.NO_SESSION_FOUND);
        this.isSaving = false;
        return;
      }

      // Validate required fields
      this.fieldErrors = {};
      if (!this.clientId) this.fieldErrors['clientId'] = 'Client is required';
      if (!this.packageDefinitionId) this.fieldErrors['packageDefinitionId'] = 'Package is required';
      if (!this.functionalAreaId) this.fieldErrors['functionalAreaId'] = 'Functional Area is required';
      if (!this.testTypeId) this.fieldErrors['testTypeId'] = 'Test Type is required';
      if (!this.scenarioIdField.trim()) this.fieldErrors['scenarioIdField'] = 'Scenario ID is required';
      if (!this.runName.trim()) this.fieldErrors['runName'] = 'Scenario Name is required';

      if (Object.keys(this.fieldErrors).length > 0) {
        this.messageService.error('Please fill all required fields');
        this.isSaving = false;
        return;
      }

      const canCreateScenario = this.packageDefinitionId && this.functionalAreaId && this.testTypeId;

      const requestIsNonDefault = this.clientId && this.clientId !== 'default';
      const ownershipMismatch = requestIsNonDefault && this.editRecordingId &&
          this.loadedRecordingClientId !== this.clientId;
      if (ownershipMismatch) {
        this.editRecordingId = '';
      }

      // Step 1: Save recording to recordings table
      const payload: any = {
        id: this.editRecordingId || null,
        recordingId: session.recordingId,
        runName: session.runName,
        description: session.description,
        baseUrl: session.baseUrl,
        steps: session.executableSteps,
        dataParameters: session.dataParameters,
        metadata: session.metadata,
        rawActions: session.actions,
        clientId: (this.clientId && this.clientId !== 'default') ? this.clientId : null,
        packageDefinitionId: this.packageDefinitionId,
        functionalAreaId: this.functionalAreaId,
        testTypeId: this.testTypeId,
        scenarioId: this.scenarioId || null,
        scenarioCode: this.scenarioIdField || null,
        isSkeleton: this.isSkeleton
      };

      this.testRecorderService.saveRecording(payload).subscribe({
        next: (savedRecording: any) => {
          if (this.editRecordingId) {
            this.messageService.success(ValidationMessagesHelper.RECORDING_UPDATED);
          } else if (this.isCloneMode) {
            this.messageService.success(ValidationMessagesHelper.RECORDING_CLONED);
          } else {
            this.messageService.success(ValidationMessagesHelper.RECORDING_SAVED);
          }

          // Save as skeleton if toggle is ON
          if (this.isSkeleton && savedRecording?.id) {
            const faMatch = (this.functionalAreaOptions || []).find((f: any) => f.id === this.functionalAreaId);
            const faName = faMatch?.name || faMatch?.functionalAreaName || this.functionalAreaId;
            const ttMatch = (this.testTypeOptions || []).find((t: any) => t.id === this.testTypeId);
            const ttName = ttMatch?.referenceValue || ttMatch?.name || this.testTypeId;
            const skeletonPayload = {
              name: session!.runName || this.runName,
              module: faName,
              testType: ttName,
              recordingId: savedRecording.id,
              description: this.description || `Skeleton from ${session!.runName || this.runName}`,
              scenarioCount: this.liveSteps.length > 0 ? 1 : 0,
              totalSteps: this.liveSteps.length,
              status: 'active'
            };
            this.skeletonService.createSkeleton(skeletonPayload as any)
              .then(() => this.messageService.success('Saved as master skeleton'))
              .catch(() => this.messageService.error('Failed to save as skeleton'));
          }

          // Step 2: Create or update scenario and steps if all required fields are present
          if (canCreateScenario && this.liveSteps.length > 0) {
            // Check if we're updating an existing scenario or creating a new one
            if (this.scenarioId) {
              // UPDATE EXISTING SCENARIO
              const updatePayload = {
                scenarioId: this.scenarioIdField || undefined,
                scenarioName: this.runName,
                scenarioDescription: this.description || `Recorded test - ${this.runName}`,
                url: this.url
 
              };

              this.scenarioService.updateScenario(this.scenarioId, updatePayload).subscribe({
                next: (scenario: any) => {
                  this.messageService.success('Scenario updated successfully');

                  // Step 3: Convert recording steps to database steps format
                  const stepsPayload = this.liveSteps.map((step, index) => {
                    const stepDescription = step.description || `${step.action} on ${step.text || step.selector?.name || 'element'}`;

                    // Parse step description to generate placeholder template
                    const parsed = this.stepPlaceholderService.parseStepDescription(stepDescription);

                    // Merge parsed additionalInfo with existing step data
                    // Note: spread parsed first, then override with real values to preserve actual password
                    const additionalInfoObj = {
                      ...parsed.additionalInfo,  // Spread parsed values first
                      recordedStep: true,
                      action: step.action,
                      selector: step.selector,
                      value: step.value,  // This overwrites parsed.additionalInfo.value with real value
                      text: step.text,
                      timeout: step.timeout || 10000,
                      displayValue: (step as any).displayValue
                    };

                    return {
                      scenarioId: this.scenarioId,
                      stepId: index + 1,
                      stepDescription: stepDescription,  // Keep actual value: "Enter the username as stud_isu"
                      placeholder: parsed.placeholder || null,  // Use template: "Enter the username as {{value}}"
                      additionalInfo: JSON.stringify(additionalInfoObj),
                      isRecording: true,
                      isActive: true
                    };
                  });
                  const bulkStepPayload = {
                    scenarioId: this.scenarioId,
                    recordingId: savedRecording.id,
                    steps: stepsPayload,
                    deleteOldSteps: true
                  };

                  this.stepsService.saveBulkStep(bulkStepPayload).subscribe({
                    next: () => {
                      this.messageService.success(`Successfully updated ${stepsPayload.length} steps for scenario`);
                      this.finishSaveAndNavigate();
                    },
                    error: (err: any) => {
                      this.messageService.error('Failed to update steps: ' + (err.error?.message || err.message || 'Unknown error'));
                      this.finishSaveAndNavigate();
                    }
                  });
                },
                error: (err: any) => {
                  this.messageService.error('Failed to update scenario: ' + (err.error?.message || err.message || 'Unknown error'));
                  this.finishSaveAndNavigate();
                }
              });
            } else {
              // Check if a scenario already exists for this recording before creating
              this.scenarioService.getScenarioByRecordingId(savedRecording.id).subscribe({
                next: (existingScenario: any) => {
                  // Scenario exists — switch to UPDATE
                  this.scenarioId = existingScenario.id;
                  this.messageService.info('Existing scenario found — updating...');
                  const updatePayload = {
                    scenarioId: this.scenarioIdField || undefined,
                    scenarioName: this.runName,
                    scenarioDescription: this.description || `Recorded test - ${this.runName}`,
                    url: this.url
                  };
                  this.scenarioService.updateScenario(existingScenario.id, updatePayload).subscribe({
                    next: () => {
                      this.messageService.success('Scenario updated successfully');
                      this.saveStepsToScenario(existingScenario.id, savedRecording.id, true);
                    },
                    error: (err: any) => {
                      this.messageService.error('Failed to update scenario: ' + (err.error?.message || err.message));
                      this.finishSaveAndNavigate();
                    }
                  });
                },
                error: () => {
                  // No existing scenario — CREATE NEW
                  this.createNewScenario(savedRecording.id);
                }
              });
            }
          } else {
            // No scenario creation, just finish
            if (!canCreateScenario) {
              this.messageService.info('Recording saved. Fill package/functional area/test type to create executable scenario.');
            }
            this.finishSaveAndNavigate();
          }
        },
        error: () => {
          this.messageService.error(ValidationMessagesHelper.FAILED_SAVE_RECORDING);
          this.isSaving = false;
        }
      });

    } catch (error: any) {
      this.messageService.error(ValidationMessagesHelper.getFailedSaveRecording(error.message));
      this.isSaving = false;
    }
  }

  private createNewScenario(recordingId: string): void {
    this.messageService.info('Creating scenario from recording...');

    const scenarioPayload = {
      packageId: this.packageDefinitionId,
      functionalAreaId: this.functionalAreaId,
      testTypeId: this.testTypeId,
      scenarioId: this.scenarioIdField || undefined,
      scenarioName: this.runName,
      scenarioDescription: this.description || `Recorded test - ${this.runName}`,
      url: this.url,
      recordingId: recordingId
    };

    this.scenarioService.createScenario(scenarioPayload).subscribe({
      next: (scenario: any) => {
        this.messageService.success('Scenario created successfully');
        this.scenarioId = scenario.id;
        this.saveStepsToScenario(scenario.id, recordingId);
      },
      error: (err: any) => {
        this.messageService.error('Failed to create scenario: ' + (err.error?.message || err.message || 'Unknown error'));
        this.finishSaveAndNavigate();
      }
    });
  }

  private saveStepsToScenario(scenarioId: string, recordingId: string, replaceExisting: boolean = false): void {
    const stepsPayload = this.liveSteps.map((step, index) => {
      const stepDescription = step.description || `${step.action} on ${step.text || step.selector?.name || 'element'}`;
      const parsed = this.stepPlaceholderService.parseStepDescription(stepDescription);
      const additionalInfoObj = {
        ...parsed.additionalInfo,
        recordedStep: true,
        action: step.action,
        selector: step.selector,
        value: step.value,
        text: step.text,
        timeout: step.timeout || 10000,
        displayValue: (step as any).displayValue
      };

      return {
        scenarioId: scenarioId,
        stepId: index + 1,
        stepDescription: stepDescription,
        placeholder: parsed.placeholder || null,
        additionalInfo: JSON.stringify(additionalInfoObj),
        isRecording: true,
        isActive: true
      };
    });

    this.stepsService.saveBulkStep({
      scenarioId: scenarioId,
      recordingId: recordingId,
      steps: stepsPayload,
      deleteOldSteps: replaceExisting || this.deleteOldSteps
    }).subscribe({
      next: () => {
        this.messageService.success(`Successfully saved ${stepsPayload.length} steps to scenario`);
        this.finishSaveAndNavigate();
      },
      error: (err: any) => {
        this.messageService.error('Failed to save steps: ' + (err.error?.message || err.message || 'Unknown error'));
        this.finishSaveAndNavigate();
      }
    });
  }

  private finishSaveAndNavigate(): void {
    this.editRecordingId = '';
    this.isCloneMode = false;
    if (this.isSystemUser()) {
      this.filterClient = 'default';
    } else if (!this.isClientFilterDisabled) {
      this.filterClient = '';
    }
    this.filterPackage = '';
    this.filterFunctionalArea = '';
    this.filterTestType = '';
    this.searchTerm = '';
    this.currentPage = 1;
    this.actionRecorder.clearSession();
    this.disableBrowserView();
    this.navigateAfterCloseOrSave();
    this.isSaving = false;
  }

  async playRecording(): Promise<void> {
    if (this.liveSteps.length === 0) {
      this.messageService.error(ValidationMessagesHelper.NO_STEPS_TO_EXECUTE);
      return;
    }

    this.isExecuting = true;
    this.stopRequested = false;
    this.currentExecutingStep = -1;
    this.executedSteps.clear();
    this.failedSteps.clear();
    this.stepErrors.clear();
    this.stepResults.clear();

    this.messageService.info(ValidationMessagesHelper.getStartingExecution(this.liveSteps.length));

    let successCount = 0;
    let failCount = 0;

    for (let i = 0; i < this.liveSteps.length; i++) {
      if (this.stopRequested) {
        this.messageService.info(ValidationMessagesHelper.EXECUTION_STOPPED_BY_USER);
        break;
      }
      const step = this.materializeValue(this.liveSteps[i]);
      this.currentExecutingStep = i;

      try {
        let result: any;
        if (step.action === 'wait') {
          const waitMs = Number(step.value) || Number(step.timeout) || 0;
          await this.sleep(waitMs);
          result = { success: true, action: 'wait', duration: waitMs };
        } else {
          result = await (window as any).host?.executeStep(step);
        }

        const actualResult = result?.result || result;
        const assertion = actualResult?.assertion;
        
        // For verification actions, check assertion.passed instead of success
        const isVerificationAction = step.action === 'verify' || step.action === 'assert' ||
                                     step.action === 'verifyNotExist' || step.action === 'assertNotExist' ||
                                     step.action === 'verifyTable' || step.action === 'verifyTableCell' ||
                                     step.action === 'verifyTableRows' || step.action === 'verifyTableColumn' ||
                                     step.action === 'verifyTableColumns' || step.action === 'verifyTableMultiple' ||
                                     step.action === 'verifyTableNotContains' || step.action === 'verifyTableNotInColumn';
        
        const verificationPassed = assertion?.passed !== undefined ? assertion.passed : true;
        const actionSuccess = actualResult?.success ?? result?.success;
        const isSuccess = isVerificationAction ? verificationPassed : actionSuccess;

        if (isSuccess) {
          this.executedSteps.add(i);
          successCount++;
          this.stepResults.set(i, result);
        } else {
          this.failedSteps.add(i);
          const errorMsg = actualResult?.error || result?.error || assertion?.message || 'Execution failed';
          this.stepErrors.set(i, errorMsg);
          failCount++;
          this.stepResults.set(i, result);

          const isVerify = step.action === 'verify' || 
                          step.action === 'verifyNotExist' ||
                          step.action === 'verifyTable' || step.action === 'verifyTableCell' || 
                          step.action === 'verifyTableRows' || step.action === 'verifyTableColumn' || 
                          step.action === 'verifyTableColumns' || step.action === 'verifyTableMultiple' ||
                          step.action === 'verifyTableNotContains' || step.action === 'verifyTableNotInColumn';

          if (isVerify) {
            this.messageService.info(ValidationMessagesHelper.getStepVerificationFailed(i, errorMsg));
          } else if ((step as any).optional) {
            this.messageService.info(`Step ${i + 1} failed (optional, continuing): ${errorMsg}`);
          } else {
            this.messageService.error(ValidationMessagesHelper.getStepFailed(i, errorMsg));
            break;
          }
        }

        await this.sleep(500);
        if (this.stopRequested) {
          this.messageService.info(ValidationMessagesHelper.EXECUTION_STOPPED_BY_USER);
          break;
        }

      } catch (error: any) {
        this.failedSteps.add(i);
        this.stepErrors.set(i, error.message || 'Unknown error');
        failCount++;
        this.stepResults.set(i, { success: false, error: error.message || 'Unknown error' });

        const isVerify = step.action === 'verify' || 
                        step.action === 'verifyNotExist' ||
                        step.action === 'verifyTable' || step.action === 'verifyTableCell' || 
                        step.action === 'verifyTableRows' || step.action === 'verifyTableColumn' || 
                        step.action === 'verifyTableColumns' || step.action === 'verifyTableMultiple' ||
                        step.action === 'verifyTableNotContains' || step.action === 'verifyTableNotInColumn';

        if (isVerify) {
          this.messageService.info(ValidationMessagesHelper.getStepVerificationError(i, error.message));
        } else if ((step as any).optional) {
          this.messageService.info(`Step ${i + 1} failed (optional, continuing): ${error.message}`);
        } else {
          this.messageService.error(ValidationMessagesHelper.getStepThrewError(i, error.message));
          break;
        }
      }
    }

    this.currentExecutingStep = -1;
    this.isExecuting = false;

    if (failCount === 0) {
      this.messageService.success(ValidationMessagesHelper.getAllStepsSuccess(successCount));
    } else if (successCount > 0) {
      this.messageService.info(ValidationMessagesHelper.getExecutionCompleted(successCount, failCount));
    } else {
      this.messageService.error(ValidationMessagesHelper.getExecutionFailed(failCount));
    }
  }

  requestStopExecution(): void {
    if (!this.isExecuting) return;
    this.stopRequested = true;
    this.messageService.info(ValidationMessagesHelper.STOP_REQUESTED);
  }

  addWaitStep(): void {
    if (this.isExecuting) return;
    const input = window.prompt('Enter wait time in milliseconds', '1000');
    if (input == null) return;
    const ms = Number(input);
    if (Number.isNaN(ms) || ms < 0) {
      this.messageService.error(ValidationMessagesHelper.ENTER_VALID_NUMBER);
      return;
    }
    this.actionRecorder.addWait(ms);
    this.messageService.success(ValidationMessagesHelper.getWaitStepAdded(ms));
  }

  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  get verifyTargetLabel(): string {
    const el: any = this.lastCapturedElement;
    if (!el) return '—';
    return el.labelText || el.name || el.ariaLabel || el.placeholder || el.text || el.tag || 'element';
  }

  get verifyTargetDetails(): string {
    const el: any = this.lastCapturedElement;
    if (!el) return '';
    const bits: string[] = [];
    if (el.tag) bits.push(el.tag.toLowerCase());
    if (el.automationId) bits.push(`data-automation-id="${el.automationId}"`);
    if (el.name) bits.push(`name="${el.name}"`);
    if (el.ariaLabel) bits.push(`aria-label="${el.ariaLabel}"`);
    if (el.placeholder) bits.push(`placeholder="${el.placeholder}"`);
    return bits.join(' • ');
  }

  deleteStep(index: number, event: Event): void {
    event.stopPropagation();

    this.actionRecorder.deleteStep(index);
    this.messageService.info(ValidationMessagesHelper.STEP_DELETED);
  }

  editStepDescription(index: number, newDescription: string): void {
    this.actionRecorder.updateStep(index, { description: newDescription });
  }

  editStepValue(index: number): void {
    const step = this.liveSteps[index];
    if (!step) return;
    if (step.action === 'select') {
      const current = step.value || '';
      const next = window.prompt('Option to select (value) – enter the exact option text:', current);
      if (next === null) return;
      this.actionRecorder.updateStep(index, { value: next || '' });
      this.messageService.info(ValidationMessagesHelper.OPTION_TO_SELECT_UPDATED);
      return;
    }
    if (step.action === 'click') {
      const currentText = step.text || '';
      const nextText = window.prompt('Target text (visible label) – enter the text to click:', currentText);
      if (nextText === null) return;
      this.actionRecorder.updateStep(index, { text: nextText || '' });
      this.messageService.info(ValidationMessagesHelper.TARGET_TEXT_UPDATED);
      return;
    }
    const current = step.value || '';
    const next = window.prompt('Enter new value (leave empty to clear):', current);
    if (next === null) return;
    this.actionRecorder.updateStep(index, { value: next || '' });
    this.messageService.info(ValidationMessagesHelper.STEP_VALUE_UPDATED);
  }

  editStepText(index: number): void {
    const current = this.liveSteps[index]?.text || '';
    const next = window.prompt('Target text (dropdown field / visible label):', current);
    if (next === null) return;
    this.actionRecorder.updateStep(index, { text: next || '' });
    this.messageService.info(ValidationMessagesHelper.TARGET_TEXT_UPDATED);
  }

  editStepOperator(index: number): void {
    const current = (this.liveSteps[index]?.selector as any)?.operator || 'contains';
    const next = window.prompt('Operator (equals | contains | containsAll | matches | is):', current) || current;
    const allowed = ['equals', 'contains', 'containsAll', 'matches', 'is'];
    const op = next.trim();
    if (!allowed.includes(op)) {
      this.messageService.error(ValidationMessagesHelper.INVALID_OPERATOR);
      return;
    }
    const selector = { ...(this.liveSteps[index]?.selector || {}) } as any;
    selector.operator = op as any;
    this.actionRecorder.updateStep(index, { selector });
    this.messageService.info(ValidationMessagesHelper.STEP_OPERATOR_UPDATED);
  }

  cloneStep(index: number): void {
    this.actionRecorder.cloneStep(index);
    this.messageService.success(ValidationMessagesHelper.STEP_COPIED);
  }

  editStepValueOnly(index: number): void {
    const step = this.liveSteps[index];
    if (!step) return;
    const current = step.value || '';
    const next = window.prompt('Enter new value (leave empty to clear):', current);
    if (next === null) return;
    this.actionRecorder.updateStep(index, { value: next || '' });
    this.messageService.info(ValidationMessagesHelper.STEP_VALUE_UPDATED);
  }

  openEditMenu(event: MouseEvent, index: number): void {
    event.stopPropagation();
    this.editMenuIndex = index;
    const step = this.liveSteps[index];
    this.editTargetText = step?.text || '';
    this.editValue = step?.value || '';
    const checkInfo = (step as any)?.checkData || (step as any)?.data || {};
    let tag = checkInfo.text || checkInfo.rowLabel || step?.text || '';
    let category = checkInfo.category || '';
    // Fallback: parse from description "Check TagName [Category]"
    if (!tag && step?.description) {
      const descMatch = step.description.match(/^(?:Check|Uncheck)\s+(.+?)(?:\s+\[(.+?)\])?$/);
      if (descMatch) {
        tag = descMatch[1] || '';
        category = category || descMatch[2] || '';
      }
    }
    if (!category && step?.description) {
      const catMatch = step.description.match(/\[(.+?)\]/);
      if (catMatch) category = catMatch[1];
    }
    this.editCheckTag = tag;
  }

  async recordNewStepAfter(index: number): Promise<void> {
    if (this.isExecuting) return;
    this.addMenuIndex = -1; this.editMenuIndex = -1; this.editTargetText = ''; this.editValue = '';
    this.actionRecorder.pendingInsertIndex = null;
    try {
      await (window as any).host?.recordingEnable(true, { recordingId: `insert-record-${Date.now()}` });
    } catch (_) { }
    this.actionRecorder.beginInsertRecord(index);
    this.insertRecordIndex = index;
    this.messageService.info(ValidationMessagesHelper.getInsertRecordPrompt(index));
    try { if (this.insertRecordSub) { this.insertRecordSub.unsubscribe(); } } catch (_) { }
    this.actionRecorder.resetInsertRecordComplete();
    this.insertRecordSub = this.actionRecorder.insertRecordComplete.subscribe(async (pos) => {
      if (pos === null) return;
      this.insertRecordIndex = -1;
      try { await (window as any).host?.recordingEnable(false); } catch (_) { }
      this.messageService.success(ValidationMessagesHelper.NEW_STEP_INSERTED);
      try { this.insertRecordSub.unsubscribe(); } catch (_) { }
      this.insertRecordSub = undefined;
    });
  }

  saveEditTargetText(index: number, panel?: any): void {
    const step = this.liveSteps[index];
    if (!step) return;
    this.actionRecorder.updateStep(index, { text: this.editTargetText || '' });
    if (step.action === 'select') {
      this.messageService.success(ValidationMessagesHelper.TARGET_TEXT_UPDATED_DROPDOWN);
    } else {
      this.messageService.success(ValidationMessagesHelper.TARGET_TEXT_UPDATED);
    }
    if (panel?.hide) panel.hide();
  }

  saveEditCheckData(index: number): void {
    const step = this.liveSteps[index];
    if (!step) return;
    const tag = this.editCheckTag.trim();
    const description = `Check ${tag}`;
    const checkData = { text: tag, rowLabel: tag };
    this.actionRecorder.updateStep(index, {
      text: tag,
      description,
      data: { text: tag, rowLabel: tag, value: '' },
    } as any);
    (this.liveSteps[index] as any).checkData = checkData;
    this.messageService.success('Check step updated');
  }

  openWaitMenu(index: number): void {
    if (this.isExecuting) return;
    this.addMenuIndex = -1;
    this.waitMenuIndex = index;
  }

  addWaitBefore(index: number, ms: number): void {
    if (this.isExecuting) return;
    this.actionRecorder.addWaitBefore(index, ms);
    this.waitMenuIndex = -1;
    this.messageService.success(ValidationMessagesHelper.getWaitAddedBeforeStep(ms, index));
  }

  addWaitBeforeCustom(index: number): void {
    if (this.isExecuting) return;
    const ms = Number(this.waitCustomMs);
    if (Number.isNaN(ms) || ms < 0) {
      this.messageService.error(ValidationMessagesHelper.ENTER_VALID_NUMBER);
      return;
    }
    this.actionRecorder.addWaitBefore(index, Math.floor(ms));
    this.waitMenuIndex = -1;
    this.messageService.success(ValidationMessagesHelper.getWaitAddedBeforeStep(Math.floor(ms), index));
  }

  addWaitBeforeFromCustom(index: number): void {
    if (this.isExecuting) return;
    const ms = Number(this.waitCustomMs);
    if (Number.isNaN(ms) || ms < 0) {
      this.messageService.error(ValidationMessagesHelper.ENTER_VALID_NUMBER);
      return;
    }
    this.actionRecorder.addWaitBefore(index, Math.floor(ms));
    this.waitMenuIndex = -1;
    this.messageService.success(ValidationMessagesHelper.getWaitAddedBeforeStep(Math.floor(ms), index));
  }

  closeWaitMenu(): void {
    this.waitMenuIndex = -1;
  }

  moveStepUp(index: number): void {
    if (this.isExecuting || index <= 0) return;
    this.actionRecorder.moveStep(index, index - 1);
  }

  moveStepDown(index: number): void {
    if (this.isExecuting || index >= this.liveSteps.length - 1) return;
    this.actionRecorder.moveStep(index, index + 1);
  }

  trackStepBy(index: number, step: any): string {
    return step.id || index.toString();
  }

  toggleOptional(index: number): void {
    const step = this.liveSteps[index];
    if (!step) return;
    this.actionRecorder.updateStep(index, { optional: !(step as any).optional });
  }

  openAddMenu(index: number): void {
    if (this.isExecuting) return;
    this.addMenuIndex = this.addMenuIndex === index ? -1 : index;
    this.waitMenuIndex = -1;
    this.actionRecorder.pendingInsertIndex = null;
  }

  closeAddMenu(): void {
    this.addMenuIndex = -1;
    this.actionRecorder.pendingInsertIndex = null;
  }

  addPressEnterAfter(index: number): void {
    if (this.isExecuting) return;
    this.actionRecorder.addPressEnterAfter(index);
    this.addMenuIndex = -1;
    this.messageService.success(`Press Enter step added after step ${index + 1}`);
  }

  scrollToVerification(index: number): void {
    this.addMenuIndex = -1;
    this.actionRecorder.pendingInsertIndex = index + 1;
    const el = document.querySelector('.verification-section');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    this.messageService.info(`Add a verification step — it will be inserted after step ${index + 1}`);
  }

  saveEditValue(index: number, panel?: any): void {
    const step = this.liveSteps[index];
    if (!step) return;
    this.actionRecorder.updateStep(index, { value: this.editValue || '' });
    if (step.action === 'select') {
      this.messageService.success(ValidationMessagesHelper.OPTION_TO_SELECT_UPDATED);
    } else {
      this.messageService.success(ValidationMessagesHelper.STEP_VALUE_UPDATED);
    }
    if (panel?.hide) panel.hide();
  }

  async runSingleStep(index: number): Promise<void> {
    const step = this.materializeValue(this.liveSteps[index]);
    if (!step || this.isExecuting) return;
    try {
      this.stepResults.delete(index);
      this.currentExecutingStep = index;
      let result: any;
      if (step.action === 'wait') {
        const waitMs = Number(step.value) || Number(step.timeout) || 0;
        await this.sleep(waitMs);
        result = { success: true, action: 'wait', duration: waitMs };
      } else {
        result = await (window as any).host?.executeStep(step);
      }
      const actualResult = result?.result || result;
      const assertion = actualResult?.assertion;
      
      // For verification actions, check assertion.passed instead of success
      const isVerificationAction = step.action === 'verify' || step.action === 'assert' ||
                                   step.action === 'verifyNotExist' || step.action === 'assertNotExist' ||
                                   step.action === 'verifyTable' || step.action === 'verifyTableCell' ||
                                   step.action === 'verifyTableRows' || step.action === 'verifyTableColumn' ||
                                   step.action === 'verifyTableColumns' || step.action === 'verifyTableMultiple' ||
                                   step.action === 'verifyTableNotContains' || step.action === 'verifyTableNotInColumn';
      
      const verificationPassed = assertion?.passed !== undefined ? assertion.passed : true;
      const actionSuccess = actualResult?.success ?? result?.success;
      const isSuccess = isVerificationAction ? verificationPassed : actionSuccess;

      if (isSuccess) {
        this.executedSteps.add(index);
        this.failedSteps.delete(index);
        this.stepErrors.delete(index);
        this.stepResults.set(index, result);
        this.messageService.success(ValidationMessagesHelper.STEP_EXECUTED_SUCCESSFULLY);
      } else {
        this.failedSteps.add(index);
        this.executedSteps.delete(index);
        const errorMsg = actualResult?.error || result?.error || assertion?.message || 'Execution failed';
        this.stepErrors.set(index, errorMsg);
        this.stepResults.set(index, result);
        this.messageService.error(ValidationMessagesHelper.getStepFailedWithError(errorMsg));
      }
    } catch (e: any) {
      this.failedSteps.add(index);
      this.executedSteps.delete(index);
      this.stepErrors.set(index, e?.message || 'Execution error');
      this.stepResults.set(index, { success: false, error: e?.message || 'Execution error' });
      this.messageService.error(ValidationMessagesHelper.getStepExecutionError(e?.message));
    } finally {
      this.currentExecutingStep = -1;
    }
  }

  private materializeValue(origStep: TestStep | undefined): TestStep {
    if (!origStep) return origStep as any;
    const step = { ...origStep };
    if (typeof step.value === 'string') {
      const m = step.value.match(/^\{\{\s*([a-zA-Z0-9_]+)\s*\}\}$/);
      if (m) {
        const param = m[1];
        const preset = this.actionRecorder.getParameterValue(param);
        if (preset && preset.length > 0) {
          step.value = preset;
        }
      }
      if (step.value === '********') {
        const display: string = (step as any).displayValue || '';
        const dm = display.match(/^\{\{\s*([a-zA-Z0-9_]+)\s*\}\}$/);
        const param = dm ? dm[1] : 'password';
        const preset = this.actionRecorder.getParameterValue(param);
        if (preset && preset.length > 0) {
          step.value = preset;
        } else {
          const entered = window.prompt(`Enter value for ${param}`, '');
          if (entered && entered.length > 0) {
            step.value = entered;
            this.actionRecorder.setParameterValue(param, entered);
          }
        }
      }
    }
    return step;
  }

  getMaskedValue(step: TestStep): string {
    const anyStep: any = step as any;
    if (anyStep.displayValue) return anyStep.displayValue;
    const looksPassword = /password|pwd/i.test(step.selector?.name || '') || /password|pwd/i.test(step.description || '');
    if (looksPassword && step.value) return '{{password}}';
    return step.value || '';
  }

  formatValidationEntry(validation: { columnPairs: Array<{ column: string; value: string; operator?: string }> }): string {
    if (!validation?.columnPairs?.length) return '(empty)';
    return validation.columnPairs.map(pair => {
      const op = pair.operator || 'equals';
      if (op === 'isEmpty' || op === 'isNotEmpty') {
        return `${pair.column} ${op}`;
      }
      return `${pair.column} ${op} "${pair.value}"`;
    }).join(', ');
  }

  getActionIcon(action: string): string {
    const icons: Record<string, string> = {
      'fill': 'pi-pencil',
      'click': 'pi-hand-pointer',
      'select': 'pi-list',
      'check': 'pi-check-square',
      'uncheck': 'pi-square',
      'navigate': 'pi-compass',
      'wait': 'pi-clock',
      'pressEnter': 'pi-sign-in',
      'hover': 'pi-eye'
    };
    return icons[action] || 'pi-circle';
  }

  getActionSeverity(action: string): string {
    const severities: Record<string, string> = {
      'fill': 'info',
      'click': 'success',
      'select': 'warning',
      'navigate': 'secondary',
      'wait': 'info',
      'hover': 'secondary'
    };
    return severities[action] || 'info';
  }

  async go(): Promise<void> {
    if (this.url) {
      await (window as any).host?.navigate(this.url);
    }
  }

  async back(): Promise<void> {
    await (window as any).host?.back();
  }

  async forward(): Promise<void> {
    await (window as any).host?.forward();
  }

  async reload(): Promise<void> {
    await (window as any).host?.reload();
  }

  startDrag(evt: MouseEvent): void {
    this.dragging = true;
    evt.preventDefault();
  }

  @HostListener('window:mousemove', ['$event'])
  onMove(evt: MouseEvent): void {
    if (!this.dragging) return;
    const next = Math.max(300, evt.clientX);
    this.splitPx = next;
    (window as any).host?.setSplit(this.splitPx);
  }

  @HostListener('window:mouseup')
  onUp(): void {
    this.dragging = false;
  }

  async cancel(): Promise<void> {
    if (this.isRecording) {
      await this.actionRecorder.stopRecording();
    }
    this.actionRecorder.clearSession();
    this.messageService.info(ValidationMessagesHelper.RECORDING_CANCELLED);
    this.navigateAfterCloseOrSave();

  }

  getFormattedTime(timestamp: string): string {
    const date = new Date(timestamp);
    return date.toLocaleTimeString();
  }

  async openRecorder(): Promise<void> {
    this.editRecordingId = '';
    this.isCloneMode = false;
    this.runName = '';
    this.description = '';
    this.url = 'https://impl.wd12.myworkday.com/wday/authgwy/erpa_amu1/login.htmld';
    this.clientId = '';
    this.clientPackageMappings = [];
    this.packageDefinitionId = '';
    this.functionalAreaId = '';
    this.testTypeId = '';
    this.actionRecorder.clearSession();
    this.router.navigate(['/test-studio/recorder-record']);
    this.isExecuting = false;
    this.currentExecutingStep = -1;
    this.executedSteps.clear();
    this.failedSteps.clear();
    this.stepErrors.clear();
    this.stepResults.clear();
    await this.initializeSplitView();
  }


  onGlobalFilter(_dt: any, event: any): void {
    const value = (event.target as HTMLInputElement).value;
    if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
    const trimmed = (value || '').trim();
    if (trimmed.length > 0 && trimmed.length < 3) return;
    this.searchDebounceTimer = setTimeout(() => {
      this.searchTerm = value;
      this.currentPage = 1;
      this.loadRecordings();
    }, 700);
  }

  onLazyLoad(event: any): void {
    this.currentPage = Math.floor((event.first || 0) / (event.rows || this.pageSize)) + 1;
    this.pageSize = event.rows || this.pageSize;
    if (event.sortField) {
      this.sortField = event.sortField;
      this.sortOrder = event.sortOrder === 1 ? 'ASC' : 'DESC';
    }
    if (!this.filtersReady) return;
    this.loadRecordings();
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.loadRecordings();
  }

  onClientFilterChange(): void {
    this.filterPackage = '';
    this.filterTestType = '';
    this.filterFunctionalArea = '';
    this.filteredFunctionalAreaList = [];
    this.filteredTestTypeList = [];
    this.currentPage = 1;
    this.loadClientPackages();
    this.loadFilterFunctionalAreas();
    this.loadRecordings();
  }

  private loadFilterFunctionalAreas(): void {
    if (!this.filterClient) {
      this.filteredFunctionalAreaList = [];
      this.filteredTestTypeList = [];
      return;
    }
    this.testRecorderService.getRecordingFilterOptions(this.filterClient, this.filterPackage || undefined, this.filterTestType || undefined).subscribe({
      next: (options) => {
        this.filteredFunctionalAreaList = options.functionalAreas || [];
        this.filteredTestTypeList = options.testTypes || [];
      },
      error: () => { this.filteredFunctionalAreaList = []; this.filteredTestTypeList = []; }
    });
  }

  onPackageFilterChange(): void {
    this.filterTestType = '';
    this.filterFunctionalArea = '';
    this.currentPage = 1;
    this.loadFilterFunctionalAreas();
    this.loadRecordings();
  }

  onTestTypeFilterChange(): void {
    this.filterFunctionalArea = '';
    this.currentPage = 1;
    this.loadFilterFunctionalAreas();
    this.loadRecordings();
  }

  private loadClientPackages(): void {
    if (!this.filterClient) {
      this.packageOptions = [];
      return;
    }
    if (this.editRecordingId && (this.router.url || '').includes('/test-studio/recorder-record')) {
      return;
    }
    this.testRecorderService.getClientPackages(this.filterClient).subscribe((packages: any) => {
      const pkgList = packages || [];
      // 'default' returns Package[] directly; specific client returns ClientPackageMapping[] with .package
      this.packageOptions = pkgList.map((item: any) => item.package || item).filter((p: any) => p && p.id);
      this.applyDefaultsFromParams();
    });
  }


  viewRecording(recording: any): void {
    this.messageService.info(ValidationMessagesHelper.getViewRecording(recording.runName));
  }


  async editRecording(recording: any): Promise<void> {
    try {
      const fullRecording = await firstValueFrom(
        this.testRecorderService.getRecordingById(recording.id)
      );
      this.router.navigate(['/test-studio/recorder-record'], {
        state: {
          recording: fullRecording,
          editRecordingId: recording.id,
          isCloneMode: false
        }
      });
    } catch {
      this.messageService.error('Failed to load recording details');
    }
  }


  public deleteRecording(recording: any): void {
    if (!recording?.id) {
      this.messageService.error(ValidationMessagesHelper.RECORDING_DELETE_FAILURE);
      return;
    }

    this.scenarioService.getScenarioByRecordingId(recording.id).subscribe({
      next: (scenario: any) => {
        this.showDeleteConfirmation(recording, !!scenario);
      },
      error: () => {
        this.showDeleteConfirmation(recording, false);
      }
    });
  }

  private showDeleteConfirmation(recording: any, hasScenario: boolean): void {
    const message = hasScenario
      ? ValidationMessagesHelper.RECORDING_SCENARIO_CONFIRM
      : 'Are you sure you want to delete ' + recording.runName + '?';

    this.confirmationService.confirm({
      message,
      header: MESSAGE_CONFIG.CONFIRM,
      acceptLabel: 'Yes',
      rejectLabel: 'No',
      acceptButtonStyleClass: 'p-button-primary',
      rejectButtonStyleClass: 'p-button-warn',
      accept: () => {
        this.delete(recording.id).then(() => {
          this.loadRecordings();
          if (hasScenario) {
              this.messageService.success(ValidationMessagesHelper.RECORDING_DELETE_SUCCESS);
            this.messageService.info(ValidationMessagesHelper.RECORDING_REMOVED_CREATING_NEW);
           
          } else {
            this.messageService.success(ValidationMessagesHelper.RECORDING_DELETE_SUCCESS);
          }
        }).catch(() => {
          this.messageService.error(ValidationMessagesHelper.RECORDING_DELETE_FAILURE);
        });
      }
    });
  }

  loadRecordings(): void {
    this.disableBrowserView();
    this.isLoadingRecordings = true;
    if (this.loadRecordingsSub) {
      this.loadRecordingsSub.unsubscribe();
    }
    this.loadRecordingsSub = this.testRecorderService.getRecordingsPaginated({
      page: this.currentPage,
      pageSize: this.pageSize,
      search: this.searchTerm || undefined,
      client: this.filterClient || undefined,
      package: this.filterPackage || undefined,
      functionalArea: this.filterFunctionalArea ? this.filterFunctionalArea.split('|')[0] : undefined,
      testType: this.filterTestType || (this.filterFunctionalArea ? this.filterFunctionalArea.split('|')[1] || undefined : undefined),
      sortBy: this.sortField,
      sortOrder: this.sortOrder,
    }).subscribe({
      next: (result: any) => {
        this.recordings = result.data;
        this.totalRecordings = result.total;
        this.isLoadingRecordings = false;
      },
      error: () => {
        this.messageService.error(ValidationMessagesHelper.FAILED_LOAD_RECORDINGS);
        this.isLoadingRecordings = false;
      }
    });
  }

  get filteredRecordings(): any[] {
    return this.recordings || [];
  }

  async onCancel(): Promise<void> {
    this.navigateAfterCloseOrSave();
    this.actionRecorder.clearSession();
    this.editRecordingId = '';
    this.isCloneMode = false;
    if (this.isSystemUser()) {
      this.filterClient = 'default';
    } else if (!this.isClientFilterDisabled) {
      this.filterClient = '';
    }
    this.filterPackage = '';
    this.filterFunctionalArea = '';
    this.filterTestType = '';
    this.searchTerm = '';
    this.currentPage = 1;
    this.disableBrowserView();
    this.loadRecordings();
  }

  addPageTextVerification(assert: boolean = false): void {
    const expected = (this.pageVerifyText || '').trim();
    if (!expected) {
      this.messageService.error(ValidationMessagesHelper.ENTER_PAGE_TEXT_TO_VERIFY);
      return;
    }
    this.actionRecorder.addPageVerification(expected, assert, 8000);

    this.pageVerifyText = '';
    this.messageService.success(ValidationMessagesHelper.getPageTextVerificationAdded(assert));
  }

  addLabelValueVerification(assert: boolean = false): void {
    const label = (this.labelVerifyLabel || '').trim();
    const expected = (this.labelVerifyExpected || '').trim();
    if (!label) {
      this.messageService.error(ValidationMessagesHelper.ENTER_LABEL_TO_VERIFY);
      return;
    }
    if (!expected) {
      this.messageService.error(ValidationMessagesHelper.ENTER_EXPECTED_VALUE);
      return;
    }
    this.actionRecorder.addLabelValueVerification({
      label,
      expected,
      operator: this.labelVerifyOperator,
      assert,
      timeoutMs: 8000
    });

    this.labelVerifyExpected = '';
    this.messageService.success(ValidationMessagesHelper.getLabelValueVerificationAdded(assert));
  }

  // Element Absence Validation Methods
  
  addElementNotExist(assert: boolean = false): void {
    const elementName = (this.notExistElementName || '').trim();
    if (!elementName) {
      this.messageService.error('Please enter the element name to verify absence');
      return;
    }
    
    const timeout = this.absenceTimeout || 3000;
    
    this.actionRecorder.addNotExistVerification({
      elementName,
      assert,
      absenceTimeout: timeout,
      description: `${assert ? 'Assert' : 'Verify'} ${elementName} does not exist`
    });


    this.notExistElementName = '';
    this.absenceTimeout = 3000;
    this.messageService.success(
      `${assert ? 'Assert' : 'Verify'} not-exist validation added for "${elementName}"`,
      'Validation Added'
    );
  }

  addPageNotContain(assert: boolean = false): void {
    const text = (this.pageNotContainText || '').trim();
    if (!text) {
      this.messageService.error('Please enter the text that should not appear on the page');
      return;
    }
    
    this.actionRecorder.addPageNotContainVerification(text, assert, 3000);


    this.pageNotContainText = '';
    this.messageService.success(
      `${assert ? 'Assert' : 'Verify'} page not-contain validation added for "${text}"`,
      'Validation Added'
    );
  }

  addTableNotContains(): void {
    const text = (this.tableNotContainText || '').trim();
    if (!text) {
      this.messageService.error('Please enter the value that should not be in the table');
      return;
    }
    
    this.actionRecorder.addTableNotContainsValidation({
      searchText: text,
      searchAllPages: this.tableNotContainSearchAllPages,
      description: `Verify table does not contain "${text}"${this.tableNotContainSearchAllPages ? ' across all pages' : ''}`
    });


    this.tableNotContainText = '';
    this.tableNotContainSearchAllPages = false;
    this.messageService.success(
      `Table not-contains validation added for "${text}"`,
      'Validation Added'
    );
  }

  addColumnNotContains(): void {
    const columnName = (this.columnNotContainName || '').trim();
    const value = (this.columnNotContainValue || '').trim();
    
    if (!columnName) {
      this.messageService.error('Please enter the column name');
      return;
    }
    
    if (!value) {
      this.messageService.error('Please enter the value that should not be in the column');
      return;
    }
    
    this.actionRecorder.addTableColumnNotInValidation({
      columnIdentifier: columnName,
      searchText: value,
      searchAllPages: this.columnNotContainSearchAllPages,
      description: `Verify column "${columnName}" does not contain "${value}"${this.columnNotContainSearchAllPages ? ' across all pages' : ''}`
    });


    this.columnNotContainName = '';
    this.columnNotContainValue = '';
    this.columnNotContainSearchAllPages = false;
    this.messageService.success(
      `Column not-contains validation added for "${columnName}"`,
      'Validation Added'
    );
  }

  // Table Validation Methods
  
  /**
   * Load existing table validation data when editing a recording
   */
  private loadExistingTableValidationData(): void {
    if (!this.liveSteps || this.liveSteps.length === 0) {
      return;
    }

    // Find first verifyTableMultiple step
    const tableValidationStep = this.liveSteps.find((step: TestStep) => 
      step.action === 'verifyTableMultiple'
    );

    if (!tableValidationStep) {
      return;
    }

    // Extract validation data
    const selector = tableValidationStep.selector || {};
    const tableSelector = selector.tableSelector || 'table';
    const validations = selector.validations || [];
    const searchAllPages = selector.searchAllPages || false;

    // Get column names from first validation
    const firstValidation = validations[0];
    const columnNames = firstValidation?.columnPairs 
      ? firstValidation.columnPairs.map((pair: any) => pair.column)
      : [];

    // Populate table info
    this.tableInfo = {
      tableSelector: tableSelector,
      tableId: tableSelector,
      columnNames: columnNames,
      columnCount: columnNames.length,
      hasPagination: searchAllPages
    };

    // Populate current validations
    this.currentValidations = validations.map((validation: any) => ({
      columnPairs: validation.columnPairs || []
    }));

    // Set flags
    this.tableSearchAllPages = searchAllPages;
    this.tableValidationActive = true;

    this.messageService.info(
      `Loaded ${this.currentValidations.length} table validation(s) for editing.`,
      'Table Validation Loaded'
    );
  }
  
  async discoverTables(): Promise<void> {
    this.isDiscoveringTables = true;
    try {
      const result = await (window as any).host?.discoverTables();
      if (result?.success && result.tables?.length > 0) {
        this.availableTables = result.tables;
        if (result.tables.length === 1) {
          this.selectedTableIndex = 0;
        } else {
          this.selectedTableIndex = -1;
        }
        this.messageService.success(`Found ${result.tables.length} table(s) on page`);
      } else {
        this.availableTables = [];
        this.selectedTableIndex = -1;
        this.messageService.error(result?.error || 'No tables found on this page');
      }
    } catch (error: any) {
      this.availableTables = [];
      this.messageService.error(`Error discovering tables: ${error.message}`);
    } finally {
      this.isDiscoveringTables = false;
    }
  }

  onTableSelected(): void {
    // No-op — selectedTableIndex is bound via ngModel
  }

  async captureTable(): Promise<void> {
    this.isCapturingTable = true;
    const table = this.availableTables.find(t => t.index === this.selectedTableIndex);
    const captureOptions = table ? { selector: table.selector, tableIndex: table.index } : undefined;
    try {
      if (this.currentTableStepId) {
        // EDITING — only fetch table metadata, don't create new step
        const tableData = await (window as any).host?.captureTable(captureOptions);
        if (!tableData || !tableData.success) {
          this.messageService.error(tableData?.error || 'Failed to capture table');
          return;
        }
        this.tableInfo = tableData;
        this.tableValidationActive = true;
        this.tableSearchAllPages = tableData.hasPagination || false;
        this.newValidationPairs = [{ column: '', value: '', operator: 'equals' }];
      } else {
        // NEW — create step + fetch metadata
        const result = await this.actionRecorder.startTableValidation(captureOptions);
        if (!result.success) {
          this.messageService.error(result.error || 'Failed to capture table');
          return;
        }
        this.tableInfo = result.tableData!;
        this.currentTableStepId = result.stepId!;
        this.tableValidationActive = true;
        this.tableSearchAllPages = result.tableData?.hasPagination || false;
        this.currentValidations = [];
        this.newValidationPairs = [{ column: '', value: '', operator: 'equals' }];
      }

      // Log multi-value detection
      if (this.tableInfo?.hasMultiValueColumns) {
        this.messageService.info(
          `Table captured with ${this.tableInfo?.columnCount} columns. ${this.tableInfo.multiValueColumns?.length} column(s) contain multiple values.`,
          'Table Analysis Complete'
        );
      } else {
        this.messageService.success(`Table captured with ${this.tableInfo?.columnCount || 0} columns`);
      }
    } catch (error: any) {
      this.messageService.error(`Error capturing table: ${error.message}`);
    } finally {
      this.isCapturingTable = false;
    }
  }

  addColumnValuePair(): void {
    this.newValidationPairs.push({ column: '', value: '', operator: 'equals' });
  }

  removePair(index: number): void {
    if (this.newValidationPairs.length > 1) {
      this.newValidationPairs.splice(index, 1);
    }
  }

  canAddValidation(): boolean {
    return this.newValidationPairs.some(pair => {
      // Valid if has column AND (has value OR is isEmpty/isNotEmpty operator)
      return pair.column && (pair.value.trim() || pair.operator === 'isEmpty' || pair.operator === 'isNotEmpty');
    });
  }

  addTableValidationEntry(): void {
    // Filter valid pairs: must have column, and either have value OR be isEmpty/isNotEmpty operator
    const validPairs = this.newValidationPairs.filter(pair => {
      return pair.column && (pair.value.trim() || pair.operator === 'isEmpty' || pair.operator === 'isNotEmpty');
    });
    
    if (validPairs.length === 0) {
      this.messageService.error('Please add at least one column-value pair');
      return;
    }

    const success = this.actionRecorder.addValidationEntry(validPairs, this.currentTableStepId);
    
    if (success) {
      // Update local validations list
      this.currentValidations.push({ columnPairs: validPairs });

      // Reset form for next entry
      this.newValidationPairs = [{ column: '', value: '', operator: 'equals' }];

      this.messageService.success('Validation entry added');
    } else {
      this.messageService.error('Failed to add validation entry');
    }
  }
  
  /**
   * Save all table validations as a verifyTableMultiple step
   * Updates existing step if in edit mode, creates new step otherwise
   */
  saveAllTableValidations(): void {
    if (!this.tableInfo) {
      this.messageService.error('Please capture a table first.');
      return;
    }

    if (this.currentValidations.length === 0) {
      this.messageService.error('Please add at least one validation entry.');
      return;
    }

    // Find the target step by currentTableStepId (not just first verifyTableMultiple)
    let existingStepIndex = -1;
    if (this.currentTableStepId) {
      existingStepIndex = this.liveSteps.findIndex((step: TestStep) => step.id === this.currentTableStepId);
    }

    // Build the verifyTableMultiple step
    const step: TestStep = {
      id: this.currentTableStepId || `table-verify-${Date.now()}`,
      action: 'verifyTableMultiple',
      selector: {
        tableSelector: this.tableInfo.tableSelector,
        columnNames: this.tableInfo.columnNames || [],
        validations: this.currentValidations.map(v => ({
          columnPairs: v.columnPairs
        })),
        searchAllPages: this.tableSearchAllPages
      },
      description: `Verify table data (${this.currentValidations.length} validation${this.currentValidations.length !== 1 ? 's' : ''})`,
      text: `Table validation (${this.currentValidations.length} validation${this.currentValidations.length !== 1 ? 's' : ''})`
    } as TestStep;

    if (existingStepIndex >= 0) {
      // Update the SPECIFIC step (not first verifyTableMultiple)
      this.liveSteps[existingStepIndex] = step;
      
      // Update in ActionRecorder service as well
      try {
        const recorderService = this.actionRecorder as any;
        if (recorderService.liveExecutableSteps$) {
          recorderService.liveExecutableSteps$.next([...this.liveSteps]);
        }
      } catch (error) {
      }

      this.messageService.success(
        `Updated table validation with ${this.currentValidations.length} entries.`,
        'Validation Updated'
      );
    } else {
      // Add new step by appending to liveSteps
      this.liveSteps.push(step);
      
      // Update in ActionRecorder service
      try {
        const recorderService = this.actionRecorder as any;
        if (recorderService.liveExecutableSteps$) {
          recorderService.liveExecutableSteps$.next([...this.liveSteps]);
        }
      } catch (error) {
      }

      this.messageService.success(
        `Added table validation with ${this.currentValidations.length} entries.`,
        'Validation Added'
      );
    }

    // Reset table validation state
    this.resetTableValidation();
  }

  removeValidation(index: number): void {
    const success = this.actionRecorder.removeValidationEntry(index, this.currentTableStepId);
    
    if (success) {
      this.currentValidations.splice(index, 1);
      
      // If editing mode, also update the step in liveSteps
      const existingStepIndex = this.liveSteps.findIndex((step: TestStep) => 
        step.action === 'verifyTableMultiple'
      );
      
      if (existingStepIndex >= 0 && this.currentValidations.length > 0) {
        // Update the step with remaining validations
        this.liveSteps[existingStepIndex].selector.validations = this.currentValidations.map(v => ({
          columnPairs: v.columnPairs
        }));
        
        try {
          const recorderService = this.actionRecorder as any;
          if (recorderService.liveExecutableSteps$) {
            recorderService.liveExecutableSteps$.next([...this.liveSteps]);
          }
        } catch (error) {
        }
      } else if (existingStepIndex >= 0 && this.currentValidations.length === 0) {
        // Remove the step if no validations left
        this.liveSteps.splice(existingStepIndex, 1);
        
        try {
          const recorderService = this.actionRecorder as any;
          if (recorderService.liveExecutableSteps$) {
            recorderService.liveExecutableSteps$.next([...this.liveSteps]);
          }
        } catch (error) {
        }
      }
      
      this.messageService.info('Validation removed');
    }
  }

  updateTableSearchAllPages(): void {
    this.actionRecorder.updateTableSearchAllPages(this.tableSearchAllPages);
  }

  editTableValidation(index: number): void {
    const step = this.liveSteps[index];
    if (!step || step.action !== 'verifyTableMultiple') return;

    const selector = step.selector || {};
    const storedColumnNames = (selector as any).columnNames || [];
    const validations = (selector as any).validations || [];
    const searchAllPages = (selector as any).searchAllPages || false;

    // Derive column names from existing validation column pairs if not stored
    let columnNames = storedColumnNames;
    if (columnNames.length === 0 && validations.length > 0) {
      const colSet = new Set<string>();
      validations.forEach((v: any) => {
        (v.columnPairs || []).forEach((cp: any) => {
          if (cp.column) colSet.add(cp.column);
        });
      });
      columnNames = Array.from(colSet);
    }

    // Populate table info
    this.tableInfo = {
      tableSelector: (selector as any).tableSelector || '[data-automation-id="table"]',
      tableId: (selector as any).tableSelector || 'table',
      columnNames: columnNames,
      columnCount: columnNames.length,
      hasPagination: searchAllPages
    };

    // Populate current validations for editing (deep copy)
    this.currentValidations = validations.map((v: any) => ({
      columnPairs: (v.columnPairs || []).map((cp: any) => ({ ...cp }))
    }));

    this.currentTableStepId = step.id || '';
    this.tableValidationActive = true;
    this.tableSearchAllPages = searchAllPages;
    this.newValidationPairs = [{ column: '', value: '', operator: 'equals' }];
    this.availableTables = [];
    this.selectedTableIndex = -1;

    this.messageService.info(`Editing table validation with ${validations.length} entries.`);
  }

  editValidationEntry(index: number): void {
    const validation = this.currentValidations[index];
    if (!validation) return;

    // Prefill the form with the existing validation's column pairs
    this.newValidationPairs = validation.columnPairs.map((cp: any) => ({
      column: cp.column || '',
      value: cp.value || '',
      operator: cp.operator || 'equals'
    }));

    // Remove the entry from current validations (will be re-added on save)
    this.currentValidations.splice(index, 1);

    // Also remove from the step in the service
    this.actionRecorder.removeValidationEntry(index, this.currentTableStepId);

    this.messageService.info('Editing validation entry — modify and click "Add Validation Entry" to save.');
  }

  async refreshTableColumns(): Promise<void> {
    try {
      const result = await (window as any).host?.discoverTables();
      if (result?.success && result.tables?.length > 0) {
        // If only one table, auto-select it
        if (result.tables.length === 1) {
          const table = result.tables[0];
          if (this.tableInfo) {
            this.tableInfo.columnNames = table.columnNames;
            this.tableInfo.columnCount = table.columnNames.length;
          }
          this.messageService.success(`Columns refreshed: ${table.columnNames.length} columns found`);
        } else {
          // Multiple tables — let user pick, then update columns
          this.availableTables = result.tables;
          this.selectedTableIndex = -1;
          this.messageService.info(`${result.tables.length} tables found. Select one to refresh columns.`);
          // Show discover UI but keep validations
          this.tableValidationActive = false;
        }
      } else {
        this.messageService.error('No tables found on page');
      }
    } catch (error: any) {
      this.messageService.error(`Error refreshing columns: ${error.message}`);
    }
  }

  resetTableValidation(): void {
    this.tableValidationActive = false;
    this.tableInfo = null;
    this.currentValidations = [];
    this.newValidationPairs = [{ column: '', value: '', operator: 'equals' }];
    this.currentTableStepId = '';
  }

  /**
   * Auto-capture table when table interaction is detected
   * This can be called from the toast notification or automatically
   */
  async autoCapturTableFromLastInteraction(): Promise<void> {
    const session = this.currentSession;
    if (!session || !session.metadata) return;
    
    const lastTableInteraction = (session.metadata as any).lastTableInteraction;
    if (!lastTableInteraction) return;

    // Use the stored table selector to capture the table
    await this.captureTable();
    
    // Auto-populate first validation entry with the clicked cell data
    if (this.tableValidationActive && lastTableInteraction.columnName) {
      this.newValidationPairs = [{
        column: lastTableInteraction.columnName,
        value: lastTableInteraction.cellValue || '',
        operator: 'equals'
      }];
      
      this.messageService.info('Pre-filled validation with clicked cell data');
    }
  }

  async cloneRecording(recording: any): Promise<void> {
    try {
      const fullRecording = await firstValueFrom(
        this.testRecorderService.getRecordingById(recording.id)
      );
      const clonedRecording = {
        ...fullRecording,
        runName: fullRecording.runName + ' (Clone)'
      };

      this.router.navigate(['/test-studio/recorder-record'], {
        state: {
          recording: clonedRecording,
          editRecordingId: '',
          isCloneMode: true
        }
      });
    } catch {
      this.messageService.error('Failed to load recording details');
    }
  }

  getReferenceList() {
    // Fetch ALL reference values including PACKAGE and FUNCTIONAL_AREA to create mappings
    this.testRecorderService.getReferenceList(["PACKAGE", "FUNCTIONAL_AREA", "TEST_TYPE"]).subscribe((allReferenceValues: any) => {
      // Store ALL reference values for ID mapping
      this.allReferenceValues = allReferenceValues;
      this.testTypeOptions = allReferenceValues.filter((r: any) => r.referenceValueData.code === "TEST_TYPE");

      // After options load, try to map names from params to IDs and patch fields
      this.applyDefaultsFromParams();
    });

    // Load clients for the Client dropdown
    this.testRecorderService.getAllClients().subscribe({
      next: (response: any) => {
        const clients = Array.isArray(response) ? response : (response?.data || []);
        this.clientOptions = [{ id: 'default', name: 'Master Client' }, ...clients];
      },
      error: () => {
        this.clientOptions = [{ id: 'default', name: 'Master Client' }];
      }
    });

    // Packages are loaded via loadClientPackages() based on selected client

    // Fetch clients for filter dropdown
    this.testRecorderService.getAllClients().subscribe((clients: any) => {
      const clientList = clients?.data || clients || [];
      const isSingle = !this.isSystemUser() && clientList.length === 1;
      this.clientOptions = this.isSystemUser()
        ? [{ id: 'default', name: 'Master Client' }, ...clientList]
        : clientList;
      this.filterClient = this.isSystemUser() ? 'default' : (isSingle ? clientList[0].id : '');
      this.isClientFilterDisabled = isSingle;
      this.filtersReady = true;
      this.loadClientPackages();
      this.loadFilterFunctionalAreas();
      if (!(this.router.url || '').includes('/test-studio/recorder-record')) {
        this.loadRecordings();
      }
    });

    this.testRecorderService.getFunctionalAreas().subscribe((functionalAreas: any) => {
      this.functionalAreaOptions = functionalAreas;
      this.applyDefaultsFromParams();
    });
  }

  // Applies defaults based on query params without impacting existing flows
  private applyDefaultsFromParams(): void {
    // Patch Scenario Name & Description
    if (this._defaultScenarioName) {
      this.runName = this._defaultScenarioName;
    }
    if (this._defaultScenarioDescription) {
      this.description = this._defaultScenarioDescription;
    }
    if (this._defaultPackageName && (!this.packageDefinitionId || this.packageDefinitionId.length === 0)) {
      const pkg = (this.packageOptions || []).find((p: any) => (p.packageName || '').toLowerCase() === this._defaultPackageName.toLowerCase());
      if (pkg?.id) {
        this.packageDefinitionId = pkg.id;
        this.packageId = pkg.id; // Sync packageId as well
      }
    }

    // Functional Area: Verify the ID exists in loaded options (even if already set)
    if (this.functionalAreaId && this.functionalAreaOptions.length > 0) {
      const faExists = this.functionalAreaOptions.some((f: any) => f.id === this.functionalAreaId);
      if (!faExists) {
        this.functionalAreaId = '';
      }
    } else if (this._defaultFunctionalAreaId && (!this.functionalAreaId || this.functionalAreaId.length === 0)) {
      // Fallback: use default if current is empty
      const faExists = (this.functionalAreaOptions || []).some((f: any) => f.id === this._defaultFunctionalAreaId);
      this.functionalAreaId = faExists ? this._defaultFunctionalAreaId : this.functionalAreaId;
    }

    // Test Type: resolve by name (e.g., 'Unit Tenant Build Validation')
    if (this._defaultTestTypeName && (!this.testTypeId || this.testTypeId.length === 0)) {
      const normalizedDefault = this._defaultTestTypeName.toLowerCase();
      const tt = (this.testTypeOptions || []).find((t: any) => {
        const refValue = (t.referenceValue || '').toLowerCase();
        const name = (t.name || '').toLowerCase();
        const refDataName = (t.referenceValueData?.name || '').toLowerCase();
        
        return refValue === normalizedDefault || 
               name === normalizedDefault || 
               refDataName === normalizedDefault;
      });
      if (tt?.id) {
        this.testTypeId = tt.id;
      }
    }
  }

  // Determine the right destination after save/close based on origin
  private navigateAfterCloseOrSave(): void {
    if (this.mode === 'screen-capture') {
      this.router.navigate(['/admin/screen-master']);
    } else if (this.originFrom === 'scenario') {
      // Restore query params from sessionStorage to maintain filter state
      const queryParams: any = {};
      
      const sessionClientId = sessionStorage.getItem('scenario_clientId');
      const sessionTestTypeId = sessionStorage.getItem('scenario_testTypeId');
      const sessionFunctionalAreaId = sessionStorage.getItem('scenario_functionalAreaId');
      
      // Only add query params if they exist in sessionStorage
      if (sessionClientId && sessionClientId !== 'default') {
        queryParams.clientId = sessionClientId;
      }
      
      if (sessionTestTypeId) {
        queryParams.testTypeId = sessionTestTypeId;
      }
      
      if (sessionFunctionalAreaId) {
        queryParams.functionalAreaId = sessionFunctionalAreaId;
      }
      
      // Navigate with query params if any exist, otherwise navigate without
      if (Object.keys(queryParams).length > 0) {
        this.router.navigate(['/scenario'], { queryParams });
      } else {
        this.router.navigate(['/scenario']);
      }
    } else {
      this.router.navigate(['/test-studio/recorder-list']);
    }
  }

  // Screen Capture Methods
  async captureCurrentScreen(): Promise<void> {
    try {
      this.isCapturingScreen = true;
      this.messageService.info('Capturing screen identifiers from browser...');
      
      const identifiers = await ScreenMasterHelper.detectCurrentScreen();
      this.capturedIdentifiers = identifiers;
      
      // Auto-populate if creating new
      if (!this.screenName) {
        this.screenName = identifiers.title || identifiers.mainHeading || 'Untitled Screen';
      }
      
      // Generate suggested rules
      const suggestedRules: any[] = [];
      if (identifiers.title) {
        suggestedRules.push({ type: 'title_contains', text: identifiers.title });
      }
      if (identifiers.url) {
        const urlPath = new URL(identifiers.url).pathname;
        suggestedRules.push({ type: 'url_contains', url: urlPath });
      }
      if (identifiers.mainHeading) {
        suggestedRules.push({ type: 'heading_contains', text: identifiers.mainHeading });
      }
      
      this.matchRules = suggestedRules;
      this.messageService.success(`Captured ${suggestedRules.length} suggested rules`);
      
    } catch (error: any) {
      this.messageService.error(error.message || 'Failed to capture screen');
    } finally {
      this.isCapturingScreen = false;
    }
  }

  deleteRule(index: number): void {
    this.matchRules.splice(index, 1);
    this.messageService.info('Rule deleted');
  }

  showAddRuleDialog(): void {
    this.showRuleDialog = true;
    this.editingRuleIndex = -1;
    this.ruleType = 'title_contains';
    this.ruleSelector = '';
    this.ruleText = '';
    this.ruleUrl = '';
  }

  showEditRuleDialog(index: number): void {
    const rule = this.matchRules[index];
    this.showRuleDialog = true;
    this.editingRuleIndex = index;
    this.ruleType = rule.type;
    this.ruleSelector = rule.selector || '';
    this.ruleText = rule.text || '';
    this.ruleUrl = rule.url || '';
  }

  saveRule(): void {
    const newRule: any = { type: this.ruleType };
    
    if (this.ruleType === 'element_exists') {
      newRule.selector = this.ruleSelector;
    } else if (this.ruleType === 'url_contains') {
      newRule.url = this.ruleUrl;
    } else {
      newRule.text = this.ruleText;
    }
    
    if (this.editingRuleIndex >= 0) {
      this.matchRules[this.editingRuleIndex] = newRule;
      this.messageService.success('Rule updated');
    } else {
      this.matchRules.push(newRule);
      this.messageService.success('Rule added');
    }
    
    this.showRuleDialog = false;
  }

  async startFallbackRecording(): Promise<void> {
    await this.clearRun();
    
    // Set dummy values for screen capture mode to bypass validations
    const originalScenarioId = this.scenarioIdField;
    const originalRunName = this.runName;
    
    this.scenarioIdField = 'screen-capture';
    this.runName = `Fallback for ${this.screenName || 'Screen'}`;
    
    await this.startRecording();
    
    // Restore original values after recording starts
    setTimeout(() => {
      this.scenarioIdField = originalScenarioId;
      this.runName = originalRunName;
    }, 100);
  }

  getRuleLabel(type: string): string {
    const labels: Record<string, string> = {
      'title_contains': 'Page Title Contains',
      'url_contains': 'URL Contains',
      'element_exists': 'Element Exists',
      'heading_contains': 'Panel Heading Contains',
      'breadcrumb_contains': 'Breadcrumb Contains'
    };
    return labels[type] || type;
  }

  getRuleValue(rule: any): string {
    if (rule.selector) return rule.selector;
    if (rule.text) return rule.text;
    if (rule.url) return rule.url;
    return '';
  }

  async saveScreen(): Promise<void> {
    if (!this.screenName) {
      this.messageService.warn('Screen Name is required');
      return;
    }
    
    if (this.matchRules.length === 0) {
      this.messageService.warn('At least one match rule is required');
      return;
    }
  
    
    const screen: any = {
      screenName: this.screenName,
      description: this.screenDescription,
      matchRules: this.matchRules,
      minMatch: this.minMatch,
      proceedSteps: this.liveSteps,
    };
    
    // Include id for update operations
    if (this.screenEditId) {
      screen.id = this.screenEditId;
    }
    
    try {
      await this.screenMasterService?.save(screen).toPromise();
      const action = this.screenEditId ? 'updated' : 'saved';
      this.messageService.success(`Screen ${action} successfully`);
      this.navigateAfterCloseOrSave();
    } catch ( error: any) {
      this.messageService.error(error.message || 'Failed to save screen');
    }
  }

  isSystemUser(): boolean {
    const userInfo = this.authService.getUserInfoFromToken();
    return userInfo?.isSystemUser === true;
  }
}